import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { AUTH_API } from "../config/api";
import "./SignIn.css";
import {
  Container,
  Header,
  Form,
  FormField,
  Input,
  Button,
  SpaceBetween,
  Link,
  Alert,
} from "@cloudscape-design/components";

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  const [error, setError] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, loading: authLoading, checkAndHandleAuthCookies } = useAuth();

  // Process URL parameters on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const error = searchParams.get('error');
    if (error) {
      setError(`Authentication error: ${error}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    (async () => {
      const processedCookies = await checkAndHandleAuthCookies();
      if (processedCookies) {
        console.log('Processed authentication cookies');
      }
    })();
  }, [location.search, checkAndHandleAuthCookies]);

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      const redirectTo = location.state?.from || '/';
      navigate(redirectTo, { replace: true });
    }
  }, [user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Veuillez remplir tous les champs.");
      return;
    }

    try {
      setLocalLoading(true);
      const response = await fetch(AUTH_API.login, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.requiresTotp) {
          // 2FA required: store user info and prompt for TOTP code
          setPendingUser(data.user);
          setError("");
        } else {
          await login(data);
          if (data.adexcloud_onboarding_pending || data.user?.adexcloud_onboarding_pending) {
            navigate("/profile", { replace: true });
          } else {
            navigate(location.state?.from || "/", { replace: true });
          }
        }
      } else {
        if (data.code === "email_not_verified") {
          setUnverifiedEmail(data.email || email);
          setResendMessage("");
          setError("");
        } else {
          setError(data.error || "Erreur lors de la connexion. Veuillez réessayer.");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Erreur de connexion au serveur. Veuillez réessayer plus tard.");
    } finally {
      setLocalLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage("");
    try {
      const response = await fetch(AUTH_API.resendVerification, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: unverifiedEmail }),
      });
      const data = await response.json();
      if (response.ok) {
        setResendMessage("Email de vérification renvoyé. Vérifiez votre boîte de réception.");
      } else {
        setResendMessage(data.error || "Erreur lors de l'envoi de l'email.");
      }
    } catch (error) {
      console.error("Resend verification error:", error);
      setResendMessage("Erreur de connexion au serveur. Veuillez réessayer plus tard.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!totpCode || totpCode.length < 6) {
      setError("Veuillez entrer le code à 6 chiffres.");
      return;
    }

    try {
      setLocalLoading(true);
      const response = await fetch(AUTH_API.totpValidate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: pendingUser?.user_id || pendingUser?.id, code: totpCode }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Reconstruct login data with pending user
        await login({ ...data, user: pendingUser });
        if (data.adexcloud_onboarding_pending || pendingUser?.adexcloud_onboarding_pending) {
          navigate("/profile", { replace: true });
        } else {
          navigate(location.state?.from || "/", { replace: true });
        }
      } else {
        setError(data.error || "Code TOTP invalide.");
      }
    } catch (error) {
      console.error("TOTP validation error:", error);
      setError("Erreur lors de la validation du code TOTP.");
    } finally {
      setLocalLoading(false);
    }
  };

  // If already authenticated, don't render the login form
  if (user) {
    return null;
  }

  return (
    <div className="signin-page">
      <div className="signin-bg-image" aria-hidden="true">
        <img src={`${process.env.PUBLIC_URL}/backgroundhero.png`} alt="" />
      </div>
      <div className="signin-overlay" aria-hidden="true" />

      <div className="signin-header">
        <img src="/logoadexcloud.png" alt="Adexgénie" className="signin-logo" />
        <div className="signin-title">Adexgénie</div>
        <div className="signin-subtitle">
          Build, deploy and manage AI agents that handle calls, chats, appointments and knowledge bases for your business.
        </div>
      </div>

      <div className="signin-form-overlay">
        <div className="signin-form-card">
          <Container
            header={
              <Header variant="h1">
                {pendingUser ? "Vérification 2FA" : "Sign in"}
              </Header>
            }
          >
            <SpaceBetween size="l" direction="vertical">
              {error && <Alert type="error">{error}</Alert>}
              {unverifiedEmail && (
                <Alert
                  type="warning"
                  action={
                    <Button
                      variant="primary"
                      loading={resendLoading}
                      onClick={handleResendVerification}
                    >
                      Renvoyer l'email
                    </Button>
                  }
                >
                  Votre email <strong>{unverifiedEmail}</strong> n'est pas encore vérifié.
                  Veuillez cliquer sur le lien de vérification envoyé à votre adresse.
                  {resendMessage && <div style={{ marginTop: '8px' }}>{resendMessage}</div>}
                </Alert>
              )}
              {!pendingUser ? (
                <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                  <Form
                    actions={
                      <SpaceBetween direction="vertical" size="xs">
                        <Button
                          formAction="submit"
                          variant="primary"
                          loading={localLoading || authLoading}
                          fullWidth
                        >
                          Se connecter
                        </Button>
                      </SpaceBetween>
                    }
                  >
                    <FormField
                      label="Email or Username"
                      description="Your email address or username"
                      errorIconAriaLabel="Error"
                      warningIconAriaLabel="Warning"
                    >
                      <Input
                        id="email"
                        type="text"
                        value={email}
                        onChange={(e) => setEmail(e.detail.value)}
                        placeholder="Enter your email or username"
                        disabled={localLoading || authLoading}
                      />
                    </FormField>
                    <FormField
                      label="Password"
                      description="Your Password"
                      errorIconAriaLabel="Error"
                      warningIconAriaLabel="Warning"
                    >
                      <Input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.detail.value)}
                        placeholder="Enter your Password"
                        disabled={localLoading || authLoading}
                      />
                    </FormField>
                  </Form>
                </form>
              ) : (
                <form onSubmit={handleTotpSubmit} style={{ width: "100%" }}>
                  <Form
                    actions={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          variant="link"
                          onClick={() => { setPendingUser(null); setTotpCode(""); }}
                          disabled={localLoading}
                        >
                          Retour
                        </Button>
                        <Button
                          formAction="submit"
                          variant="primary"
                          loading={localLoading}
                        >
                          Vérifier
                        </Button>
                      </SpaceBetween>
                    }
                  >
                    <FormField
                      label="Code d'authentification"
                      description="Entrez le code à 6 chiffres de votre application d'authentification"
                    >
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.detail.value.replace(/\D/g, ""))}
                        placeholder="000000"
                        disabled={localLoading}
                      />
                    </FormField>
                  </Form>
                </form>
              )}
              {!pendingUser && (
                <SpaceBetween size="xs" direction="vertical">
                  <Link href="/signup">
                    Pas encore de compte ? Inscrivez-vous
                  </Link>
                  <Link href="/forgot-password">
                    Mot de passe oublié ?
                  </Link>
                </SpaceBetween>
              )}
            </SpaceBetween>
          </Container>
        </div>
      </div>

    </div>
  );
};

export default SignIn;
